import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import {
  ForgotPasswordConfirmStep,
  ForgotPasswordStep,
  LoginHeader,
  MfaStep,
  PasswordStep,
  ResetPasswordStep,
  SetupMfaStep,
  SuccessStep,
  UsernameStep,
} from "./components";
import { useLoginFlow } from "./useLoginFlow";
import { usePasswordPolicy } from "../../hooks/usePasswordPolicy";
import { validateEmail, validateUsername } from "./validation";

const SSO_TEST_PENDING_KEY = "sso-test-pending";

export default function Login() {
  const { theme } = useTheme();
  const { data: passwordPolicy } = usePasswordPolicy();
  const {
    state,
    dispatch,
    isLoading,
    ssoInitiateMutation,
    loginMutation,
    confirmMfaMutation,
    verifyMfaMutation,
    resetPasswordMutation,
    forgotPasswordMutation,
    confirmForgotPasswordMutation,
    navigate,
  } = useLoginFlow();
  const { completeAuth, getHomeRoute } = useAuth();

  const [mfaCode, setMfaCode] = useState(["", "", "", "", "", ""]);
  const [email, setEmail] = useState("");

  // Safety net: clear stale sso-test-pending flag on login page mount.
  // If a user is on /login, they cannot be in the middle of a test connection flow.
  useEffect(() => {
    localStorage.removeItem(SSO_TEST_PENDING_KEY);
  }, []);

  const handleUsernameSubmit = (username: string) => {
    const validation = validateUsername(username);
    if (!validation.valid) {
      dispatch({ type: "SET_ERROR", error: validation.error });
      return;
    }
    dispatch({ type: "SET_USERNAME", username });
    ssoInitiateMutation.mutate(username);
  };

  const handlePasswordSubmit = async (password: string) => {
    loginMutation.mutate({ username: state.username, password });
  };

  /* v8 ignore start */
  const handleResetPasswordSubmit = async (newPassword: string) => {
    resetPasswordMutation.mutate({
      username: state.username,
      newPassword,
      session: state.session,
    });
  };

  const handleSetupMfaComplete = (code: string) => {
    confirmMfaMutation.mutate(
      {
        username: state.username,
        mfaCode: code,
        session: state.session,
      },
      {
        onError: () => setMfaCode(["", "", "", "", "", ""]),
      },
    );
  };

  const handleMfaComplete = (code: string) => {
    verifyMfaMutation.mutate(
      {
        username: state.username,
        mfaCode: code,
        session: state.session,
        challengeName: state.challengeName as "SMS_MFA" | "SOFTWARE_TOKEN_MFA",
      },
      {
        onError: () => setMfaCode(["", "", "", "", "", ""]),
      },
    );
  };
  /* v8 ignore stop */

  const handleBackToLogin = () => {
    dispatch({ type: "RESET" });
    setMfaCode(["", "", "", "", "", ""]);
  };

  const isDark = theme === "dark";

  return (
    <div
      className={`w-full h-screen relative overflow-hidden flex items-center justify-center ${
        isDark ? "bg-black" : "bg-white"
      }`}
    >
      {/* Gradient orb — bottom-left (dark: blue, light: purple/blue) */}
      <div
        className="absolute size-[1200px] pointer-events-none"
        style={{ bottom: "-571px", left: "-601px" }}
      >
        <div
          className="absolute inset-0 rounded-full blur-[200px]"
          style={{
            background: isDark
              ? "radial-gradient(circle, rgba(0,80,255,0.4) 0%, transparent 70%)"
              : "radial-gradient(circle, rgba(100,80,255,0.3) 0%, transparent 70%)",
          }}
        />
      </div>
      {/* Gradient orb — top-right (dark: green, light: green) */}
      <div
        className="absolute size-[1200px] pointer-events-none"
        style={{ top: "-629px", right: "-599px" }}
      >
        <div
          className="absolute inset-0 rounded-full blur-[200px]"
          style={{
            background: isDark
              ? "radial-gradient(circle, rgba(0,200,50,0.4) 0%, transparent 70%)"
              : "radial-gradient(circle, rgba(0,220,80,0.4) 0%, transparent 70%)",
          }}
        />
      </div>
      {/* Login card */}
      <div
        className={`relative z-10 w-[560px] px-[100px] pt-[60px] pb-[80px] rounded-[20px] flex flex-col items-center ${
          isDark
            ? "bg-[rgba(255,255,255,0.07)] drop-shadow-[0px_30px_30px_rgba(0,0,0,0.15)]"
            : "bg-white drop-shadow-[0px_30px_30px_rgba(0,0,0,0.15)]"
        }`}
      >
        <div className="w-full flex flex-col">
          <LoginHeader theme={theme} step={state.step} />
          {state.step === "username" && (
            <UsernameStep
              username={state.username}
              onUsernameChange={(username) =>
                dispatch({ type: "SET_USERNAME", username })
              }
              onSubmit={handleUsernameSubmit}
              isLoading={isLoading}
              error={state.error}
            />
          )}
          {state.step === "password" && (
            <PasswordStep
              username={state.username}
              onUsernameChange={(username) =>
                dispatch({ type: "SET_USERNAME", username })
              }
              onSubmit={handlePasswordSubmit}
              onForgotPassword={() =>
                dispatch({ type: "SET_STEP", step: "forgotPassword" })
              }
              isLoading={isLoading}
              error={state.error}
            />
          )}
          {state.step === "forgotPassword" && (
            <ForgotPasswordStep
              email={email}
              onEmailChange={setEmail}
              onSubmit={(submittedEmail) => {
                const validation = validateEmail(submittedEmail);
                if (!validation.valid) {
                  dispatch({ type: "SET_ERROR", error: validation.error });
                  return;
                }
                forgotPasswordMutation.mutate({ username: submittedEmail });
              }}
              onBackToSignIn={() => {
                dispatch({ type: "SET_STEP", step: "username" });
                setEmail("");
              }}
            />
          )}
          {state.step === "forgotPasswordConfirm" && (
            <ForgotPasswordConfirmStep
              username={email}
              onSubmit={(username, confirmationCode, newPassword) =>
                confirmForgotPasswordMutation.mutate({
                  username,
                  confirmationCode,
                  newPassword,
                })
              }
              isLoading={isLoading}
              error={state.error}
              policy={passwordPolicy}
            />
          )}
          {state.step === "resetPassword" && (
            <ResetPasswordStep
              onSubmit={handleResetPasswordSubmit}
              isLoading={isLoading}
              policy={passwordPolicy}
            />
          )}
          {state.step === "setupMfa" && (
            <SetupMfaStep
              qrCode={state.qrCode}
              mfaCode={mfaCode}
              onMfaCodeChange={setMfaCode}
              onMfaComplete={handleSetupMfaComplete}
              error={state.error}
            />
          )}
          {state.step === "mfa" && (
            <MfaStep
              mfaCode={mfaCode}
              onMfaCodeChange={setMfaCode}
              onMfaComplete={handleMfaComplete}
              onBackToLogin={handleBackToLogin}
              error={state.error}
            />
          )}
          {state.step === "forgotPasswordSuccess" && (
            <SuccessStep
              onContinue={() => dispatch({ type: "RESET" })}
              buttonLabel={{
                id: "hK3mNp",
                defaultMessage: "Go to Login ({countdown}s)",
                description:
                  "Button text to navigate to login with countdown after password reset",
              }}
            />
          )}
          {state.step === "success" && (
            <SuccessStep
              onContinue={() => {
                completeAuth();
                navigate({ to: getHomeRoute() });
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
